<template>
  <div
    class="p-6 mx-auto max-w-7xl space-y-6 bg-[#f8fafc] min-h-screen text-slate-900 font-sans"
  >
    <div
      class="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
    >
      <div>
        <h1
          class="text-2xl font-extrabold tracking-tight text-slate-900 uppercase"
        >
          Аналитика плагина
        </h1>
      </div>

      <button
        @click="fetchData"
        :disabled="loading"
        class="inline-flex items-center gap-2 px-6 py-3 bg-white border border-slate-200 rounded-2xl shadow-sm hover:bg-slate-50 transition-all active:scale-95 disabled:opacity-50"
      >
        <div
          v-if="loading"
          class="w-4 h-4 border-2 border-slate-300 border-t-slate-900 rounded-full animate-spin"
        ></div>
        <span class="text-sm font-bold text-slate-700">{{
          loading ? "Обновление..." : "Обновить ленту"
        }}</span>
      </button>
    </div>

    <div
      class="grid grid-cols-1 md:grid-cols-12 gap-4 p-5 bg-white rounded-2xl border border-slate-200 shadow-sm"
    >
      <div class="md:col-span-5 flex flex-col gap-1.5">
        <label
          class="text-[10px] font-black text-slate-400 uppercase tracking-widest"
          >Начало периода</label
        >
        <input
          type="date"
          v-model="filterFrom"
          class="w-full border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-blue-50 outline-none transition-all font-medium"
        />
      </div>
      <div class="md:col-span-5 flex flex-col gap-1.5">
        <label
          class="text-[10px] font-black text-slate-400 uppercase tracking-widest"
          >Конец периода</label
        >
        <input
          type="date"
          v-model="filterTo"
          class="w-full border border-slate-200 rounded-xl px-4 py-2.5 bg-slate-50 focus:bg-white focus:ring-4 focus:ring-blue-50 outline-none transition-all font-medium"
        />
      </div>
      <div class="md:col-span-2 flex items-end">
        <button
          @click="fetchData"
          class="w-full bg-slate-900 text-white font-bold py-3 rounded-xl hover:bg-black transition-all shadow-lg shadow-slate-200"
        >
          Применить
        </button>
      </div>
    </div>

    <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
      <div
        v-for="(stat, idx) in summaryStats"
        :key="idx"
        class="bg-white p-5 rounded-2xl border border-slate-200 shadow-sm"
      >
        <p
          class="text-[10px] font-black text-slate-400 uppercase tracking-wider"
        >
          {{ stat.label }}
        </p>
        <p class="text-3xl font-black mt-1 tabular-nums text-slate-800">
          {{ loading ? "..." : stat.value }}
        </p>
      </div>
    </div>

    <!-- Ошибка загрузки -->
    <div
      v-if="error"
      class="rounded-2xl border border-red-200 bg-red-50 p-6 text-center"
    >
      <p class="text-red-600 font-semibold">{{ error }}</p>
      <button
        @click="fetchData"
        class="mt-3 px-5 py-2 bg-red-600 text-white rounded-xl text-sm font-bold hover:bg-red-700 transition"
      >
        Повторить
      </button>
    </div>

    <template v-if="!error">
    <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div
        class="lg:col-span-2 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm"
      >
        <h3 class="font-bold text-slate-800 mb-6 flex items-center gap-2">
          <span class="w-2 h-2 bg-blue-500 rounded-full"></span>
          Распределение по клиникам
        </h3>
        <div class="h-[320px]">
          <VChart v-if="rawData.length" :option="chartOption" autoresize />
          <div
            v-else
            class="h-full flex items-center justify-center text-slate-300 text-sm italic border-2 border-dashed border-slate-50 rounded-2xl"
          >
            Нет данных за период
          </div>
        </div>
      </div>

      <div
        class="bg-white p-6 rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex flex-col"
      >
        <div class="flex items-center justify-between mb-6">
          <h3 class="font-bold text-slate-800">Последние стопы</h3>
          <div class="flex items-center gap-1.5">
            <span class="relative flex h-2 w-2">
              <span
                class="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"
              ></span>
              <span
                class="relative inline-flex rounded-full h-2 w-2 bg-red-500"
              ></span>
            </span>
            <span
              class="text-[10px] font-bold text-red-500 uppercase tracking-tighter"
              >Live</span
            >
          </div>
        </div>

        <div
          class="space-y-4 overflow-y-auto pr-2 custom-scrollbar flex-1 max-h-[320px]"
        >
          <div
            v-for="stop in recentStops"
            :key="stop.id"
            class="relative pl-4 border-l-2 border-slate-100 hover:border-blue-500 transition-all pb-2 group"
          >
            <div class="flex justify-between items-center mb-1">
              <span class="text-[10px] font-bold text-slate-400">
                {{ formatDateTime(stop.created_at) }}
              </span>
              <span
                class="text-[10px] font-mono font-black bg-blue-50 text-blue-700 border border-blue-100 px-1.5 py-0.5 rounded"
              >
                {{ stop.mkb_code || "???" }}
              </span>
            </div>
            <p
              class="text-[12px] font-bold text-slate-800 leading-tight group-hover:text-blue-600 transition-colors"
            >
              {{ stop.clinic_doctor || "Неизвестный врач" }}
            </p>
            <p
              class="text-[11px] text-slate-500 truncate mt-0.5"
              :title="stop.reason_name"
            >
              {{ stop.reason_name || "Причина не указана" }}
            </p>
            <p class="text-[9px] text-slate-300 font-bold uppercase mt-1">
              {{ stop.clinic }}
            </p>
          </div>
          <div
            v-if="recentStops.length === 0"
            class="text-center py-20 text-slate-300 italic text-sm"
          >
            Событий пока нет
          </div>
        </div>
      </div>
    </div>

    <div
      class="bg-white rounded-3xl border border-slate-200 shadow-xl overflow-hidden transition-all"
    >
      <div
        class="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white"
      >
        <div v-if="!selectedClinic && !selectedDoctor" class="relative w-full sm:w-72">
          <input
            v-model="searchClinic"
            type="text"
            placeholder="Поиск клиники..."
            class="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl text-sm outline-none focus:ring-4 focus:ring-blue-50 transition-all"
          />
          <svg
            class="w-5 h-5 absolute left-3.5 top-3 text-slate-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        <!-- Breadcrumb navigation -->
        <div v-if="selectedClinic || selectedDoctor" class="flex items-center gap-2 flex-wrap">
          <button
            @click="selectedDoctor = null; selectedClinic = null"
            class="text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors"
          >
            Все клиники
          </button>
          <span v-if="selectedClinic" class="text-slate-300">/</span>
          <button
            v-if="selectedClinic"
            @click="selectedDoctor = null"
            :class="selectedDoctor ? 'text-blue-600 hover:text-blue-800' : 'text-slate-800 cursor-default'"
            class="text-sm font-bold transition-colors"
          >
            {{ selectedClinic }}
          </button>
          <span v-if="selectedDoctor" class="text-slate-300">/</span>
          <span v-if="selectedDoctor" class="text-sm font-bold text-slate-800">
            {{ selectedDoctor }}
          </span>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table v-if="!selectedClinic && !selectedDoctor" class="w-full text-left border-spacing-0">
          <thead class="bg-slate-50/50">
            <tr>
              <th
                class="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest"
              >
                Клиника
              </th>
              <th
                class="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center"
              >
                Врачей
              </th>
              <th
                class="px-8 py-5 text-[10px] font-black text-slate-400 uppercase tracking-widest text-center"
              >
                Кол-во стопов
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr
              v-for="item in filteredClinicReport"
              :key="item.clinicName"
              @click="selectedClinic = item.clinicName"
              class="hover:bg-blue-50/40 cursor-pointer transition-all group"
            >
              <td class="px-8 py-6">
                <div class="flex items-center gap-4">
                  <span
                    class="text-sm font-extrabold text-slate-700 group-hover:text-blue-600 transition-colors"
                    >{{ item.clinicName }}</span
                  >
                </div>
              </td>
              <td
                class="px-8 py-6 text-center text-sm font-bold text-slate-500"
              >
                {{ item.doctorsCount }}
              </td>
              <td class="px-8 py-6 text-center">
                <span
                  class="inline-flex items-center px-5 py-2 rounded-2xl bg-blue-600 text-white text-xs font-black shadow-md shadow-blue-100"
                >
                  {{ item.stopsCount }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Doctor list for selected clinic -->
        <table v-else-if="selectedClinic && !selectedDoctor" class="w-full text-left border-spacing-0">
          <thead class="bg-slate-900 text-white">
            <tr>
              <th
                class="px-8 py-4 text-[10px] font-bold uppercase tracking-widest"
              >
                Врач
              </th>
              <th
                class="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-center"
              >
                Стопов
              </th>
              <th
                class="px-8 py-4 text-[10px] font-bold uppercase tracking-widest text-right"
              >
                Последний МКБ
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-slate-100">
            <tr
              v-for="doc in clinicDetailReport"
              :key="doc.doctorName"
              @click="selectedDoctor = doc.doctorName"
              class="hover:bg-blue-50/40 cursor-pointer transition-all group"
            >
              <td class="px-8 py-6">
                <p class="text-sm font-black text-slate-800 group-hover:text-blue-600 transition-colors">
                  {{ doc.doctorName }}
                </p>
              </td>
              <td class="px-8 py-6 text-center">
                <span
                  class="px-4 py-1.5 bg-slate-100 rounded-xl text-xs font-black text-slate-700"
                  >{{ doc.count }}</span
                >
              </td>
              <td class="px-8 py-6 text-right">
                <div class="flex flex-col items-end">
                  <span
                    class="px-2 py-0.5 bg-blue-50 text-blue-700 text-[10px] font-black rounded border border-blue-100 mb-1"
                    >{{ doc.lastMkb }}</span
                  >
                  <span
                    class="text-[11px] text-slate-400 italic truncate max-w-xs leading-none"
                    >{{ doc.lastReason }}</span
                  >
                </div>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Doctor detail: all stops for selected doctor -->
        <div v-else-if="selectedDoctor" class="p-0">
          <!-- Doctor summary stats -->
          <div class="p-6 border-b border-slate-100">
            <div class="bg-slate-50 rounded-2xl p-6 w-full text-center">
              <p class="text-[10px] font-black text-slate-400 uppercase tracking-wider">Всего стопов</p>
              <p class="text-4xl font-black text-slate-800 mt-2">{{ doctorDetailRecords.length }}</p>
            </div>
          </div>

          <table class="w-full text-left border-spacing-0">
            <thead class="bg-slate-900 text-white">
              <tr>
                <th class="px-6 py-4 text-[10px] font-bold uppercase tracking-widest">Дата</th>
                <th class="px-6 py-4 text-[10px] font-bold uppercase tracking-widest text-center">МКБ код</th>
                <th class="px-6 py-4 text-[10px] font-bold uppercase tracking-widest">Повод</th>
                <th class="px-6 py-4 text-[10px] font-bold uppercase tracking-widest">Время</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100">
              <tr
                v-for="record in doctorDetailRecords"
                :key="record.id"
                class="hover:bg-slate-50 transition-colors"
              >
                <td class="px-6 py-5">
                  <p class="text-sm font-bold text-slate-800">{{ formatDate(record.created_at) }}</p>
                </td>
                <td class="px-6 py-5 text-center">
                  <span class="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-black rounded-lg border border-blue-100">
                    {{ record.mkb_code || '—' }}
                  </span>
                </td>
                <td class="px-6 py-5">
                  <p class="text-sm text-slate-600">{{ record.reason_name || 'Повод не указан' }}</p>
                </td>
                <td class="px-6 py-5">
                  <p class="text-sm font-mono font-bold text-slate-700">{{ formatTime(record.created_at) }}</p>
                </td>
              </tr>
              <tr v-if="doctorDetailRecords.length === 0">
                <td colspan="4" class="px-6 py-16 text-center text-slate-300 italic text-sm">Нет данных о стопах</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { pluginService } from "@/services/pluginService";

import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import VChart from "vue-echarts";
use([CanvasRenderer, BarChart, GridComponent, TooltipComponent]);

const rawData = ref([]);
const loading = ref(false);
const error = ref(null);
const selectedClinic = ref(null);
const selectedDoctor = ref(null);
const searchClinic = ref("");
const filterFrom = ref("2026-01-01");
const filterTo = ref(new Date().toISOString().split("T")[0]);

async function fetchData() {
  loading.value = true;
  error.value = null;
  try {
    const fromVal = filterFrom.value;
    const toVal = filterTo.value;

    if (!fromVal || !toVal) return;

    const endDate = new Date(toVal);

    endDate.setDate(endDate.getDate() + 1);

    const correctedTo = endDate.toISOString().split("T")[0];

    const params = {
      from: fromVal,
      to: correctedTo,
    };

    console.log("Запрос с коррекцией даты:", params);

    const data = await pluginService.getMKBQueryStats(params);
    rawData.value = Array.isArray(data) ? data : [];
  } catch (err) {
    console.error("Ошибка:", err);
    error.value = "Не удалось загрузить данные";
    rawData.value = [];
  } finally {
    loading.value = false;
  }
}
const recentStops = computed(() => {
  return [...rawData.value]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 12);
});

const formatDateTime = (dateStr) => {
  if (!dateStr) return "--:--";

  const date = new Date(dateStr);

  return date.toLocaleString("ru-RU", {
    timeZone: "Asia/Almaty",
    hour: "2-digit",
    minute: "2-digit",
    day: "2-digit",
    month: "2-digit",
  });
};

const summaryStats = computed(() => [
  { label: "Всего стопов", value: rawData.value.length },
  {
    label: "Активных клиник",
    value: new Set(rawData.value.map((r) => r.clinic).filter(Boolean)).size,
  },
  {
    label: "Всего врачей",
    value: new Set(rawData.value.map((r) => r.clinic_doctor).filter(Boolean))
      .size,
  },
  {
    label: "Диагнозов МКБ",
    value: new Set(rawData.value.map((r) => r.mkb_code).filter(Boolean)).size,
  },
]);

const clinicReport = computed(() => {
  const map = {};
  rawData.value.forEach((row) => {
    const c = String(row.clinic || "Не указана");
    if (!map[c]) map[c] = { clinicName: c, docs: new Set(), count: 0 };
    map[c].docs.add(row.clinic_doctor || "Неизвестный");
    map[c].count++;
  });
  return Object.values(map)
    .map((i) => ({
      clinicName: i.clinicName,
      doctorsCount: i.docs.size,
      stopsCount: i.count,
    }))
    .sort((a, b) => b.stopsCount - a.stopsCount);
});

const filteredClinicReport = computed(() => {
  const q = searchClinic.value.toLowerCase().trim();
  return clinicReport.value.filter((i) =>
    i.clinicName.toLowerCase().includes(q)
  );
});

const clinicDetailReport = computed(() => {
  if (!selectedClinic.value) return [];
  const docs = {};
  const filteredData = rawData.value.filter(
    (i) => (i.clinic || "Не указана") === selectedClinic.value
  );

  filteredData.forEach((i) => {
    const n = i.clinic_doctor || "Неизвестный врач";
    if (!docs[n]) {
      docs[n] = { doctorName: n, records: [] };
    }
    docs[n].records.push(i);
  });

  return Object.values(docs)
    .map((doc) => {
      doc.records.sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
      const latest = doc.records[0] || {};
      return {
        doctorName: doc.doctorName,
        count: doc.records.length,
        lastMkb: latest.mkb_code || "???",
        lastReason: latest.reason_name || "—",
      };
    })
    .sort((a, b) => b.count - a.count);
});

const doctorDetailRecords = computed(() => {
  if (!selectedClinic.value || !selectedDoctor.value) return [];
  return rawData.value
    .filter(
      (i) =>
        (i.clinic || 'Не указана') === selectedClinic.value &&
        (i.clinic_doctor || 'Неизвестный врач') === selectedDoctor.value
    )
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
});

const doctorUniqueMkbCount = computed(() => {
  return new Set(doctorDetailRecords.value.map((r) => r.mkb_code).filter(Boolean)).size;
});

const doctorUniqueReasonCount = computed(() => {
  return new Set(doctorDetailRecords.value.map((r) => r.reason_name).filter(Boolean)).size;
});

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleDateString('ru-RU', {
    timeZone: 'Asia/Almaty',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

const formatTime = (dateStr) => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  return date.toLocaleTimeString('ru-RU', {
    timeZone: 'Asia/Almaty',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const chartOption = computed(() => ({
  tooltip: {
    trigger: "axis",
    backgroundColor: "#0f172a",
    textStyle: { color: "#fff" },
  },
  grid: {
    top: "5%",
    left: "2%",
    right: "2%",
    bottom: "15%",
    containLabel: true,
  },
  xAxis: {
    type: "category",
    data: clinicReport.value.slice(0, 10).map((i) => i.clinicName),
    axisLabel: { color: "#94a3b8", fontSize: 10, rotate: 20 },
  },
  yAxis: { type: "value", splitLine: { lineStyle: { color: "#f1f5f9" } } },
  series: [
    {
      data: clinicReport.value.slice(0, 10).map((i) => i.stopsCount),
      type: "bar",
      itemStyle: { color: "#0f172a", borderRadius: [6, 6, 0, 0] },
      barMaxWidth: 30,
    },
  ],
}));

onMounted(fetchData);
</script>

<style scoped>
.custom-scrollbar::-webkit-scrollbar {
  width: 3px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #e2e8f0;
  border-radius: 10px;
}
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>